import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';

const apiUrl = 'api/control-tower';

export interface ScoreHistoryState {
  data: IndicatorTrendsResponse | null;
  loading: boolean;
  error: string | null;
}

const initialState: ScoreHistoryState = {
  data: null,
  loading: false,
  error: null,
};

interface FetchScoreHistoryParams {
  licensePlates: string[];
  periodicType: string;
  searchCompanyId?: number | null;
  dateStart?: string | null;
  dateEnd?: string | null;
}

export interface IndicatorTrendsResponse {
  generalScores: GeneralScore[];
  vehicles: VehicleKpi[];
}

export interface GeneralScore {
  kpiName: string;
  score: number;
  date: string;
}

export interface VehicleKpi {
  vehicleId: number;
  licensePlate: string;
  kpiScores: KpiScore[];
}

export interface KpiScore {
  kpiName: string;
  score: number;
  date: string;
}

export const fetchScoreHistory = createAsyncThunk(
  'scoreHistory/fetch',
  async ({ licensePlates, periodicType, searchCompanyId, dateStart, dateEnd }: FetchScoreHistoryParams) => {
    if (!licensePlates || licensePlates.length === 0) {
      throw new Error('At least one license plate must be provided.');
    }

    const queryParams = new URLSearchParams();

    licensePlates.forEach(plate => {
      queryParams.append('licensePlates', plate);
    });

    if (searchCompanyId) {
      queryParams.append('searchCompanyId', searchCompanyId.toString());
    }

    if (dateStart) {
      queryParams.append('dateStart', dateStart);
    }

    if (dateEnd) {
      queryParams.append('dateEnd', dateEnd);
    }

    const response = await axios.get<IndicatorTrendsResponse>(`${apiUrl}/score-history/${periodicType}/?${queryParams.toString()}`);
    return response.data;
  }
);

export const scoreHistorySlice = createSlice({
  name: 'scoreHistory',
  initialState,
  reducers: {
    resetScoreHistory(state) {
      state.data = null;
      state.error = null;
      state.loading = false;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(fetchScoreHistory.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchScoreHistory.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
        state.error = null;
      })
      .addCase(fetchScoreHistory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Erro ao carregar histórico de pontuação';
      });
  },
});

export const { resetScoreHistory } = scoreHistorySlice.actions;

export default scoreHistorySlice.reducer;
